import 'bootstrap/dist/css/bootstrap.min.css';
import _ from 'lodash';
import { useEffect, useState, useMemo } from 'react';
import { Accordion, Card, Container, Navbar, Table } from 'react-bootstrap';
import apiService from '../services/ApiService';

// Define types for result data
export interface ResultTime {
  id: number;
  resultTime: string;
}

export interface Game {
  id: number;
  name: string;
  digitSupported: boolean;
  singlePattiSupported: boolean;
  burfiSupported: boolean;
  offline: boolean;
  status: string;
}

// Define types for result data
export interface Result {
  resultId: number;
  resultDate: string;
  resultTime?: ResultTime;
  gameDTO?: Game;
  singleResult: string;
  pattiResult: string;
  digitResult: string;
  burfiResult: string;
}

interface GameConfig {
  id: number;
  game: {
    id: number;
    name: string;
    digitSupported: boolean;
    burfiSupported: boolean;
    singlePattiSupported: boolean;
    offline: boolean;
    status: string;
  };
  resultTime: {
    id: number;
    resultTime: string;
  };
  limitTimeInterval: number;
  singleMin: number | null;
  singleLimitMin: number | null;
  singleMax: number | null;
  singleLimitMax: number | null;
  pattiMin: number | null;
  pattiLimitMin: number | null;
  pattiMax: number | null;
  pattiLimitMax: number | null;
  winMultiplierSingle: number | null;
  winMultiplierPatti: number | null;
  status: string;
}

const groupBySiteName = (data: Result[]): { [key: string]: Result[] } => {
  return data.reduce((groups: { [key: string]: Result[] }, item: Result) => {
    const group = groups[item.gameDTO?.name || ''] || [];
    group.push(item);
    groups[item.gameDTO?.name || ''] = group;
    return groups;
  }, {});
};

const ResultPage = () => {
  const [dataSourceTodayResult, setDataSourceTodayResult] = useState<Result[]>([]);
  const [dataSourceLastDayResult, setDataSourceLastDayResult] = useState<Result[]>([]);
  const [gameConfigs, setGameConfigs] = useState<GameConfig[]>([]);
  const [dataSourcePrevResult, setDataSourcePrevResult] = useState<Result[]>([]);
  const message = process.env.REACT_APP_DISCLAIMER || 'Welcome to Balbira!';

  let siteColorMap: { [siteName: string]: string } = {};
  siteColorMap["KALYAN BAZAR"] = '#35a723';
  siteColorMap["NAGPUR MAIN"] = "#3cdc23";
  siteColorMap["NAGMANI"] = "#2377dc";
  siteColorMap["SMARTMUMBAIMATKA"] = "rgb(252, 76, 78)";

  const bg = "rgba(245, 103, 105, 1)";  // Updated to black background
  const bgTextColor = 'white';  // White text color
  const tableBg = '#6c5c58';  // example table background
  const tableText = 'white';  // White text color for tables
  const footerBg = '#343a40';  // footer background color
  const footerText = 'white';  // footer text color
  const disclaimerBg = '#495057';  // disclaimer background color
  const disclaimerText = 'white';  // disclaimer text color

  useEffect(() => {
    // Fetch data using the apiService
    apiService.getTodayResult()
      .then(data => setDataSourceTodayResult(data))
      .catch(error => console.error('Error fetching today\'s results:', error));

    apiService.getLastDayResult()
      .then(data => setDataSourceLastDayResult(data))
      .catch(error => console.error('Error fetching last day\'s results:', error));


    apiService.getPreviousResult()
      .then(data => setDataSourcePrevResult(data))
      .catch(error => console.error('Error fetching previous results:', error));

    // Load game configs from the provided data
    const loadGameConfigs = async () => {
      try {

        apiService.getGameConfigs()
          .then(data => setGameConfigs(data))
          .catch(error => console.error('Error fetching game configs:', error));
      } catch (error) {
        console.error('Error loading game configs:', error);
      }
    };

    loadGameConfigs();
  }, []);

  // Group configs by game name
  const gameGroups = useMemo(() => {
    return _.groupBy(gameConfigs, config => config.game.name);
  }, [gameConfigs]);

  // Get unique game names
  const gameNames = useMemo(() => {
    return Object.keys(gameGroups);
  }, [gameGroups]);

  const groupedData = _.groupBy(dataSourcePrevResult, 'resultDate');

  const handleAccordionToggle = (gameName: string) => {
    
    // Get the first time ID for this game to use in the API call
    const gameConfigsForGame = gameGroups[gameName] || [];
    if (gameConfigsForGame.length > 0) {
      const gameId = gameConfigsForGame[0].game.id;
      
      apiService.getPreviousResult(gameId, 30) // Fetch last 30 days
        .then(data => {
          setDataSourcePrevResult(data);
        })
        .catch(error => {
          console.error(`Error fetching results for ${gameName}:`, error);
        })
        .finally(() => {
        });
    }
};

  return (
    <div style={{ backgroundColor: bg, color: bgTextColor }}>
      <Navbar bg="dark" variant="dark" className="mb-4" style={{ justifyContent: 'space-between', padding: '1rem 2rem' }}>
        <Navbar.Brand className='font-weight-bold' style={{ color: '#d3d3d3' }}>Balbira</Navbar.Brand> {/* Ash colored text */}
      </Navbar>

      <Container>
        {/* Today Result */}
        <section className="mb-5">
          <h5 className='font-weight-bold mb-3 text-center'>Today Result</h5>
          {Object.entries(groupBySiteName(dataSourceTodayResult)).map(([siteName, results]) => (
            <div key={siteName}>
              <div className="p-3 text-uppercase font-weight-bold" style={{ backgroundColor: siteColorMap[siteName], border: tableText }}>
                <b>{siteName}</b>
              </div>
              <div className="table-responsive">
                <Table bordered striped>
                  <thead style={{ backgroundColor: bg, color: tableText }}>
                    <tr>
                      <th>Date</th>
                      {results.map((result) => (
                        <th key={result.resultTime?.resultTime}>{result.resultTime?.resultTime}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ backgroundColor: tableBg, color: tableText }}>
                      <td>{new Date(results[0]?.resultDate).toLocaleDateString('en-GB')}</td>
                      {results.map((result) => (
                        <td key={result.resultId}>{`${result.pattiResult}*${result.singleResult}`}</td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          ))}
        </section>

        {/* Last Day Result */}
        <section className="mb-5">
          <h5 className='font-weight-bold mb-3 text-center'>Last Day Result</h5>
          {Object.entries(groupBySiteName(dataSourceLastDayResult)).map(([siteName, results]) => (
            <div key={siteName}>
              <div className="p-3 text-uppercase font-weight-bold" style={{ backgroundColor: siteColorMap[siteName] || tableBg, border: tableText }}>
                <b>{siteName}</b>
              </div>
              <div className="table-responsive">
                <Table bordered striped>
                  <thead style={{ backgroundColor: siteColorMap[siteName] || tableBg, color: tableText }}>
                    <tr>
                      <th>Date</th>
                      {results.map((result) => (
                        <th key={result.resultTime?.resultTime}>{result.resultTime?.resultTime}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ backgroundColor: tableBg, color: tableText }}>
                      <td>{new Date(results[0]?.resultDate).toLocaleDateString('en-GB')}</td>
                      {results.map((result) => (
                        <td key={result.resultId}>{`${result.pattiResult}*${result.singleResult}`}</td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          ))}
        </section>

        {/* Time Chart */}
        <section className="mb-5">
          <div className="container mt-5">
            <h5 className='font-weight-bold mb-3 text-center'>Time Chart</h5>
            {gameNames.map((gameName) => (
              <Accordion key={`time-${gameName}`} defaultActiveKey={null}>
                <Card>
                  <Accordion.Header>
                    {gameName}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table bordered striped>
                      <thead>
                        <tr style={{ backgroundColor: tableBg, color: tableText }}>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gameGroups[gameName]?.map((config, index) => (
                          <tr key={index} style={{ backgroundColor: tableBg, color: tableText }}>
                            <td>{config.resultTime.resultTime}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Card>
              </Accordion>
            ))}
          </div>
        </section>

        {/* Panel Chart */}
        <section className="mb-5">
          <h5 className='font-weight-bold mb-3 text-center'>Panel Chart</h5>
          {gameNames.map((gameName) => {
            const gameConfigsForGame = gameGroups[gameName] || [];

            return (
              <Accordion key={`panel-${gameName}`} 
                defaultActiveKey={null}
                onSelect={() => handleAccordionToggle(gameName)}>
                <Card>
                  <Accordion.Header>
                    {gameName}
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="table-responsive">
                      <Table striped bordered hover responsive className="w-100">
                        <thead style={{ backgroundColor: tableBg, color: tableText }}>
                          <tr>
                            <th>Date</th>
                            {gameConfigsForGame.map((config) => (
                              <th key={config.resultTime.id}>{config.resultTime.resultTime}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(groupedData).map(([date, dateResults]) => {
                            // Create a map of timeId to result for this date
                            const resultsByTime = (dateResults || []).reduce((acc, result) => {
                              if (result.resultTime) {
                                acc[result.resultTime.id] = result;
                              }
                              return acc;
                            }, {} as Record<number, Result>);
                            return (
                              <tr key={date} style={{ backgroundColor: tableBg, color: tableText }}>
                                <td>{new Date(date).toLocaleDateString('en-GB')}</td>
                                {gameConfigsForGame.map((config) => {
                                  const result = resultsByTime[config.resultTime.id];
                                  return (
                                    <td key={config.resultTime.id}>
                                      {result ? (
                                        <div>
                                          <div style={{verticalAlign: 'center', justifyContent: 'center', display: 'flex'}}>{result.pattiResult} 
                                             <br /> { result.singleResult}</div>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </Accordion.Body>
                </Card>
              </Accordion>
            );
          })}
        </section>
      </Container>

      {/* Footer */}
      <footer style={{ backgroundColor: footerBg, color: footerText }}>
        <Container className="text-center py-3">
          <p>Copyright © mahabirmatka.com 2020. All rights reserved.</p>
        </Container>
        <Navbar style={{ backgroundColor: disclaimerBg, color: disclaimerText }}>
          <Navbar.Text>
            <div className="marquee">
              {message}
            </div>
          </Navbar.Text>
        </Navbar>
      </footer>

      {/* CSS for Scrolling Text */}
      <style>{`
        .marquee {
          display: inline-block;
          white-space: nowrap;
          animation: marquee 10s linear infinite;
        }

        @keyframes marquee {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(-100%);
          }
        }
      `}</style>
    </div>
  );
};

export default ResultPage;
